export const CHAT_V2_PROGRESS_SCHEMA_VERSION = 2;

const CHAT_V2_PROGRESS_TEXT_LIMITS = {
    message: 180,
    title: 80,
    detail: 180,
    id: 40,
    updatedAt: 40,
};

const VALID_STEP_STATES = new Set([
    'pending',
    'running',
    'complete',
    'failed',
    'skipped',
]);

const normalizeProgressText = (value, maxLength) => {
    if (typeof value !== 'string') {
        return null;
    }

    const text = Array.from(value)
        .map((character) => {
            const codePoint = character.codePointAt(0);
            return codePoint < 32 || codePoint === 127 ? ' ' : character;
        })
        .join('')
        .replace(/\s+/g, ' ')
        .trim();

    if (!text) {
        return null;
    }

    return text.slice(0, maxLength);
};

const normalizeProgressStepId = (value) => {
    const id = normalizeProgressText(value, CHAT_V2_PROGRESS_TEXT_LIMITS.id);

    if (!id) {
        return null;
    }

    return id
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '') || null;
};

const normalizeProgressStepState = (value) => {
    const state = normalizeProgressText(value, 20)?.toLowerCase().replace(/-/g, '_');
    const normalizedState = ({
        completed: 'complete',
        done: 'complete',
        active: 'running',
        in_progress: 'running',
        error: 'failed',
        queued: 'pending',
    })[state] || state;

    return VALID_STEP_STATES.has(normalizedState) ? normalizedState : null;
};

const normalizeComparableText = (value) => String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const boundedPublicText = (value, limit) => {
    const text = normalizeProgressText(value, Infinity);
    return text && Array.from(text).length <= limit ? text : null;
};

const normalizeActivities = (value) => Array.from(new Set(
    (Array.isArray(value) ? value : [])
        .map((text) => boundedPublicText(text, 160)).filter(Boolean)
)).slice(0, 4);

const normalizeSourceGroup = (value) => {
    const ids = new Set();
    return (Array.isArray(value) ? value : []).flatMap((source) => {
        const id = boundedPublicText(source?.id, 128);
        const name = boundedPublicText(source?.name, 200);
        if (!id || !name || ids.has(id)) return [];
        let imageURL = boundedPublicText(source?.imageURL, 2048) || '';
        if (imageURL) {
            try {
                const url = new URL(imageURL);
                if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) imageURL = '';
            } catch {
                imageURL = '';
            }
        }
        const subtitle = boundedPublicText(source?.subtitle, 160);
        ids.add(id);
        return [{ id, name, imageURL, ...(subtitle ? { subtitle } : {}) }];
    });
};

const normalizeSources = (value) => {
    const speakers = normalizeSourceGroup(value.speakers);
    const podcasts = normalizeSourceGroup(value.podcasts);
    // A legacy preview array is not an authoritative count of the evidence pool.
    const count = (value, items) => Number.isSafeInteger(value) && value >= items.length && value <= 1000000
        ? value : undefined;
    const speakerCount = count(value.speakerCount, speakers);
    const podcastCount = count(value.podcastCount, podcasts);
    return {
        speakers: speakers.slice(0, 100),
        podcasts: podcasts.slice(0, 100),
        ...(speakerCount !== undefined ? { speakerCount } : {}),
        ...(podcastCount !== undefined ? { podcastCount } : {}),
    };
};

export const normalizeChatV2Progress = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }

    const state = normalizeProgressText(value.state, 20)?.toLowerCase() || null;
    const message = normalizeProgressText(value.message, CHAT_V2_PROGRESS_TEXT_LIMITS.message);
    const legacyProgress = {
        state,
        message,
    };

    if (value.schemaVersion !== CHAT_V2_PROGRESS_SCHEMA_VERSION) {
        return legacyProgress;
    }

    const steps = [];
    const seenStepIds = new Set();
    const inputSteps = Array.isArray(value.steps) ? value.steps : [];

    inputSteps.forEach((step) => {
        if (!step || typeof step !== 'object' || Array.isArray(step)) {
            return;
        }

        const id = normalizeProgressStepId(step.id);
        const stepState = normalizeProgressStepState(step.state);
        const title = normalizeProgressText(step.title, CHAT_V2_PROGRESS_TEXT_LIMITS.title);

        if (!id || !stepState || !title || seenStepIds.has(id)) {
            return;
        }

        seenStepIds.add(id);
        const rawDetail = normalizeProgressText(step.detail, CHAT_V2_PROGRESS_TEXT_LIMITS.detail);
        const detail = rawDetail && normalizeComparableText(rawDetail) !== normalizeComparableText(title) ?
            rawDetail :
            null;

        steps.push({
            id,
            state: stepState,
            title,
            detail,
        });
    });

    const currentStepId = normalizeProgressStepId(value.currentStepId);
    // Reject an oversized public sentence rather than displaying a cut-off one.
    const userMessage = boundedPublicText(value.userMessage, 240);

    return {
        ...legacyProgress,
        schemaVersion: CHAT_V2_PROGRESS_SCHEMA_VERSION,
        ...(userMessage ? { userMessage } : {}),
        ...(Array.isArray(value.activities) ? { activities: normalizeActivities(value.activities) } : {}),
        ...(value.sources && typeof value.sources === 'object' && !Array.isArray(value.sources) ? {
            sources: normalizeSources(value.sources),
        } : {}),
        currentStepId: currentStepId && seenStepIds.has(currentStepId) ? currentStepId : null,
        steps,
        updatedAt: normalizeProgressText(value.updatedAt, CHAT_V2_PROGRESS_TEXT_LIMITS.updatedAt),
    };
};
